import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_URL from '../api';
import { resolveAssetUrl } from '../utils/assetUrl';
import './FlashcardStep.css';

const FlashcardStep = ({ contentHtml, courseId, stepId, onComplete }) => {
    const [cards, setCards] = useState([]);
    const [queue, setQueue] = useState([]);
    const [mastered, setMastered] = useState(new Set());
    const [flipped, setFlipped] = useState(false);
    const [completed, setCompleted] = useState(false);
    const [parseError, setParseError] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isSrsMode, setIsSrsMode] = useState(true);

    useEffect(() => {
        setParseError(false);
        setMastered(new Set());
        setFlipped(false);
        setCompleted(false);

        if (!courseId || !stepId) {
            // Fallback for previews / offline tests
            try {
                const list = contentHtml ? JSON.parse(contentHtml) : [];
                if (!Array.isArray(list)) throw new Error('not array');
                setCards(list.map((c, i) => ({
                    ...c,
                    cardId: c.term || `card-${i}`,
                    isDue: true,
                    box: 1
                })));
                setQueue(list.map((_, i) => i));
                setLoading(false);
            } catch {
                setCards([]);
                setQueue([]);
                setParseError(true);
                setLoading(false);
            }
            return;
        }

        const fetchProgress = async () => {
            try {
                setLoading(true);
                const res = await axios.get(`${API_URL}/courses/student/flashcards/${courseId}/${stepId}`);
                const fetchedCards = res.data.cards || [];
                setCards(fetchedCards);

                // Default to SRS mode: filter cards that are due
                const dueIndices = [];
                fetchedCards.forEach((c, idx) => {
                    if (c.isDue) {
                        dueIndices.push(idx);
                    }
                });

                if (dueIndices.length > 0) {
                    setQueue(dueIndices);
                    setIsSrsMode(true);
                } else {
                    // Nothing due! Start in Full Review mode by default
                    setQueue(fetchedCards.map((_, i) => i));
                    setIsSrsMode(false);
                }
                setLoading(false);
            } catch (err) {
                console.error('Error loading flashcard progress:', err);
                setParseError(true);
                setLoading(false);
            }
        };

        fetchProgress();
    }, [courseId, stepId, contentHtml]);

    const handleModeChange = (srs) => {
        setMastered(new Set());
        setFlipped(false);
        setCompleted(false);
        setIsSrsMode(srs);

        if (srs) {
            // Filter only due cards
            const dueIndices = [];
            cards.forEach((c, idx) => {
                if (c.isDue) {
                    dueIndices.push(idx);
                }
            });
            setQueue(dueIndices);
        } else {
            // All cards
            setQueue(cards.map((_, i) => i));
        }
    };

    const masteredCount = mastered.size;
    const totalCount = isSrsMode 
        ? cards.filter(c => c.isDue).length 
        : cards.length;

    const currentCard = queue.length > 0 ? cards[queue[0]] : null;

    const handleFlip = () => {
        if (flipped || completed) return;
        setFlipped(true);
    };

    const handleAnswer = async (known) => {
        if (!flipped || completed) return;
        const currentCardIndex = queue[0];
        const cardObj = cards[currentCardIndex];

        // Trigger API post in the background
        if (courseId && stepId) {
            axios.post(`${API_URL}/courses/student/flashcards/${courseId}/${stepId}/answer`, {
                cardId: cardObj.cardId,
                known
            }).then((res) => {
                if (res.data && res.data.progress) {
                    setCards(prev => prev.map((c, i) => {
                        if (i === currentCardIndex) {
                            return {
                                ...c,
                                box: res.data.progress.box,
                                isDue: res.data.progress.isDue
                            };
                        }
                        return c;
                    }));
                }
            }).catch(err => console.error('Error updating card progress:', err));
        }

        const newMastered = new Set(mastered);
        if (known) newMastered.add(currentCardIndex);
        
        const newQueue = [...queue];
        newQueue.splice(0, 1);
        if (!known) {
            newQueue.push(currentCardIndex); // repeat until learned in session
        }

        setFlipped(false);
        setMastered(newMastered);
        setQueue(newQueue);

        if (newQueue.length === 0) {
            setCompleted(true);
        }
    };

    if (loading) {
        return (
            <div className="flashcards-empty">
                <p>Завантаження карток...</p>
            </div>
        );
    }

    if (parseError) {
        return (
            <div className="flashcards-empty">
                <p>⚠️ Не вдалося завантажити картки.</p>
            </div>
        );
    }

    if (cards.length === 0) {
        return (
            <div className="flashcards-empty">
                <p>⚠️ Картки для вивчення не налаштовані.</p>
            </div>
        );
    }

    if (completed) {
        return (
            <div className="flashcards-step-container">
                <div className="flashcard-complete">
                    <div className="flashcard-complete-icon">🎉</div>
                    <h2>{isSrsMode ? 'Повторення завершено!' : 'Всі картки вивчено!'}</h2>
                    <p>{masteredCount} з {totalCount}</p>
                    <button className="flashcard-continue-btn" onClick={() => onComplete?.()}>
                        ПРОДОВЖИТИ
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="flashcards-step-container">
            {/* Mode Selector */}
            {courseId && stepId && (
                <div className="srs-mode-selector">
                    <button
                        className={`srs-mode-btn ${isSrsMode ? 'active' : ''}`}
                        onClick={() => handleModeChange(true)}
                    >
                        Повторення ({cards.filter(c => c.isDue).length} через SRS)
                    </button>
                    <button
                        className={`srs-mode-btn ${!isSrsMode ? 'active' : ''}`}
                        onClick={() => handleModeChange(false)}
                    >
                        Повний огляд ({cards.length})
                    </button>
                </div>
            )}

            {isSrsMode && queue.length === 0 ? (
                <div className="flashcard-complete">
                    <div className="flashcard-complete-icon">🏆</div>
                    <h2>Немає карток для повторення</h2>
                    <p>Ви вже повторили всі картки на сьогодні за системою інтервальних повторень.</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', alignItems: 'center' }}>
                        <button className="srs-mode-btn active" style={{ maxWidth: '200px' }} onClick={() => handleModeChange(false)}>
                            Пройти повний огляд
                        </button>
                        <button className="flashcard-continue-btn" onClick={() => onComplete?.()}>
                            ПРОДОВЖИТИ КУРС
                        </button>
                    </div>
                </div>
            ) : (
                <>
                    <div className="flashcard-header">
                        <div>
                            <h3 className="flashcard-title-text">
                                {isSrsMode ? "Інтервальне повторення" : "Повний огляд"}
                            </h3>
                            <p className="flashcard-subtitle">{masteredCount} з {totalCount} вивчено</p>
                        </div>
                        <span className="flashcard-remaining">{queue.length} залишилось</span>
                    </div>

                    <div className="flashcard-progress-bar">
                        <div
                            className="flashcard-progress-fill"
                            style={{ width: `${totalCount > 0 ? (masteredCount / totalCount) * 100 : 0}%` }}
                        />
                    </div>

                    <div className="flashcard-card-wrapper" onClick={handleFlip}>
                        <div className={`flashcard-card-inner ${flipped ? 'is-flipped' : ''}`}>
                            <div className="flashcard-side flashcard-side-front">
                                <span className="flashcard-box-indicator">Коробка {currentCard?.box || 1}</span>
                                {currentCard?.image && (
                                    <div
                                        className="flashcard-side-image"
                                        style={{ backgroundImage: `url(${resolveAssetUrl(currentCard.image)})` }}
                                    />
                                )}
                                <div className="flashcard-side-overlay">
                                    <h2 className="flashcard-term">
                                        {currentCard?.term || 'Термін'}
                                    </h2>
                                </div>
                            </div>
                            <div className="flashcard-side flashcard-side-back">
                                <span className="flashcard-box-indicator">Коробка {currentCard?.box || 1}</span>
                                <div className="flashcard-back-body">
                                    {currentCard?.term && (
                                        <div className="flashcard-back-term">
                                            {currentCard.term}
                                            {currentCard.isDue && <span className="srs-status-badge">Due</span>}
                                        </div>
                                    )}
                                    <p className="flashcard-definition">
                                        {currentCard?.definition || 'Значення не вказано'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <p className="flashcard-flip-hint">
                        {!flipped ? 'Торкніться картки, щоб побачити відповідь' : ' '}
                    </p>

                    {flipped && (
                        <div className="flashcard-actions">
                            <button className="flashcard-btn flashcard-btn-no" onClick={() => handleAnswer(false)}>
                                ✕ НЕ ЗНАЮ
                            </button>
                            <button className="flashcard-btn flashcard-btn-yes" onClick={() => handleAnswer(true)}>
                                ✓ ЗНАЮ
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default FlashcardStep;
